import { useCallback, useMemo, useRef } from "react";

import type {
  dashboardApiEndpoints,
  ExtractFetcherReturn,
  FilteredDashboardApiEndpointKeys,
  PusherDeveloperBindings,
} from "@dashboardApi/apis";
import { QueryObserver, useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { useAppGroupId } from "@services/useAppGroupId";
import { useErrorBoundary } from "@services/useErrorBoundary";
import { useInEditorDomain } from "@services/useInEditorDomain";
import { usePusherContextV2 } from "@services/usePusherContextV2";

import { useDashboardApi } from "./useDashboardApi";
import { useEditorDomainApiPostWebsocketQueryFetcher } from "./useEditorDomainApiPostWebsocketQueryFetcher";
import { jobTokenFromParams } from "./utils";

/**
 * Hook for making imperative POSTWS (Pusher-backed websocket) requests to
 * dashboard API endpoints. Originally built for Operator tool callbacks, but
 * suited to any imperative POSTWS call outside the declarative query
 * lifecycle (e.g. the Brand Guidelines Studio generate flow).
 *
 * Combines the imperative QueryObserver pattern from
 * `useDashboardApiOperatorQuery` with the POSTWS-specific setup from
 * `useDashboardApiPostWebsocketQuery` (Pusher bindings, job-token generation,
 * editor-domain support, and the v2 adapter feature flag).
 *
 * The returned `fetch` function generates a unique job token per call, so
 * each invocation triggers a fresh backend job. Query results are removed
 * from the cache after settlement to avoid unbounded cache growth.
 *
 * @example
 * ```ts
 * const { fetch: generateImage } = useDashboardApiOperatorPostWebsocketQuery(
 *   "/:app_group_id/generate_image"
 * );
 *
 * // inside a tool callback:
 * const result = await generateImage({ args: { output_image_prompt: "a cat" } });
 * ```
 */
export const useDashboardApiOperatorPostWebsocketQuery = <
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, "POSTWS">,
>(
  endpoint: Endpoint
) => {
  const appGroupId = useAppGroupId();
  const { logException } = useErrorBoundary();
  const { POSTWS: postwsFetcherFunction } = useDashboardApi(endpoint);
  const pusherContext = usePusherContextV2();
  const inEditorDomain = useInEditorDomain();
  const postMessageFetcher = useEditorDomainApiPostWebsocketQueryFetcher(endpoint);
  const queryClient = useQueryClient();

  const pusher: PusherDeveloperBindings | undefined = useMemo(
    () =>
      inEditorDomain
        ? undefined
        : {
            bind: (eventName, callback) => {
              pusherContext.subscribeChannel({ channel: "developer", eventName, callback });
            },
            unbind: (eventName, callback) => {
              pusherContext.unsubscribeChannel({ channel: "developer", eventName, callback });
            },
          },
    [pusherContext, inEditorDomain]
  );

  // Use a ref to avoid infinite re-rendering
  const latestRef = useRef({
    logException,
    postwsFetcherFunction,
    postMessageFetcher,
    pusher,
    inEditorDomain,
  });
  latestRef.current = {
    logException,
    postwsFetcherFunction,
    postMessageFetcher,
    pusher,
    inEditorDomain,
  };

  const fetch = useCallback(
    (
      params: Record<string, unknown>
    ): Promise<ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint, "POSTWS">> => {
      const {
        logException: latestLogException,
        postwsFetcherFunction: latestPostws,
        postMessageFetcher: latestPostMessageFetcher,
        pusher: latestPusher,
        inEditorDomain: latestInEditorDomain,
      } = latestRef.current;

      const jobTokenSeed = uuid();
      const jobToken = jobTokenFromParams({ ...params, appGroupId, jobTokenSeed });
      const queryKey = [
        { cacheKey: endpoint, appGroupId },
        { ...params, jobToken, jobTokenSeed },
      ];

      const fetcherFunction = (args: any) => {
        return (latestPostws as any)(args, latestInEditorDomain ? latestPostMessageFetcher : undefined);
      };

      return new Promise<ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint, "POSTWS">>(
        (resolve, reject) => {
          let settled = false;
          const observer = new QueryObserver(queryClient, {
            queryKey,
            queryFn: fetcherFunction as any,
            retry: false,
            meta: { pusher: latestPusher, logException: latestLogException },
          });

          const unsubscribe = observer.subscribe((result) => {
            if (settled) {
              return;
            }
            if (result.status === "error") {
              settled = true;
              unsubscribe();
              queryClient.removeQueries(queryKey);
              reject(result.error);
            } else if (result.status === "success" && !result.isFetching) {
              settled = true;
              unsubscribe();
              queryClient.removeQueries(queryKey);
              resolve(result.data as ExtractFetcherReturn<typeof dashboardApiEndpoints, Endpoint, "POSTWS">);
            }
          });
        }
      );
    },
    [endpoint, appGroupId, queryClient]
  );

  return { fetch };
};
